import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import type { TFunction } from "i18next";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { bomKeys, saveBomAdjustment } from "@/api/bom";
import { getDigitalAccessCoverage, type DigitalAccessCoverage } from "@/api/digital-access";
import { SubsequentDocumentNote } from "@/components/projects/diagnostic/digitalAccess/SubsequentDocumentNote";
import { DA_DOC_TYPE_NAMES, type DaDocType } from "@/components/projects/diagnostic/selectors/digitalAccess";
import { useCurrentUser } from "@/hooks/useAuth";
import { formatCount } from "@/lib/format";
import { tp } from "@/lib/plural";
import { hasMinRole } from "@/lib/roles";
import { cn } from "@/lib/utils";

/** Debaixo do prefixo da BOM: aplicar o Pricing App ou salvar um ajuste muda a cobertura. */
const digitalAccessCoverageKey = (projectId: string) =>
  [...bomKeys.all(projectId), "digital-access-coverage"] as const;

const STATUS_STYLE: Record<string, string> = {
  covered: "bg-[#e8f3ec] text-[#2e6b45]",
  gap: "bg-[#fdf3e1] text-[#8a5a00]",
  not_contracted: "bg-[#fdecea] text-[#b42318]",
};

function statusLabel(status: string | null | undefined, t: TFunction): string {
  if (status === "covered") return t("Covered");
  if (status === "gap") return t("Coverage gap");
  return t("Not contracted");
}

function docs(value: number): string {
  return formatCount(Math.round(value));
}

/** Por que a estimativa é piso, com as palavras do consultor (#1521). */
function floorReason(code: string, t: TFunction): string {
  if (code === "budget_exhausted") return t("the time budget of the count ran out");
  if (code === "partition_full") return t("a day had more rows than the read limit");
  if (code === "timeout") return t("a read in SAP ran out of time");
  return t("an item could not be read ({{code}})", { code });
}

function Figure({ testId, label, value }: { testId: string; label: string; value: string }) {
  return (
    <div className="rounded-md border border-[#e8ebf0] bg-white px-3 py-2">
      <div className="text-[10px] uppercase tracking-wide text-[#7b8799]">{label}</div>
      <div data-testid={testId} className="text-[14px] font-semibold text-[#08254c]">
        {value}
      </div>
    </div>
  );
}

/**
 * Bloco Digital Access do nó 06 (#1346, fatia 11; EF-FUNC-005 RF-07/08).
 *
 * A estimativa anual de documentos, já com as decisões por usuário do
 * Diagnóstico, contra o que a BOM vigente dá de direito: a franquia das
 * licenças que qualificam e os blocos de Digital Access que entram no total. A
 * conta (gap e blocos sugeridos) vem do agents, a mesma da linha da BOM.
 *
 * Com gap, o consultor marca a inclusão dos blocos sugeridos: é um ajuste da
 * linha de Digital Access, que fica fora do total até o próximo upload do
 * Pricing App, como a EF-FUNC-003 trata linha que só o myGROW tem.
 */
export function DigitalAccessCoverageBlock({ projectId }: { projectId: string }) {
  const { t } = useTranslation();
  const canAct = hasMinRole(useCurrentUser()?.role, "consultant");
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: digitalAccessCoverageKey(projectId),
    queryFn: () => getDigitalAccessCoverage(projectId),
    retry: false,
  });

  const mark = useMutation({
    mutationFn: (coverage: DigitalAccessCoverage) =>
      saveBomAdjustment(projectId, {
        base_version: coverage.bom_version ?? 0,
        adjustments: [
          {
            sku_number: coverage.da_line!.sku_number,
            qty_current: coverage.suggested_blocks,
            validation_status: "adjusted",
            justification: t(
              "Digital Access gap of {{gap}} documents per year above the franchise and the contracted blocks.",
              { gap: docs(coverage.gap) },
            ),
          },
        ],
      }),
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: bomKeys.all(projectId) });
    },
    onError: (err) => {
      const detail = isAxiosError(err) ? err.response?.data?.detail : undefined;
      const message = detail && typeof detail === "object" ? detail.message ?? detail.errors?.join(" ") : detail;
      setError(typeof message === "string" ? message : t("Could not mark the Digital Access blocks."));
    },
  });

  const coverage = query.data;
  if (!coverage) return null;

  if (coverage.state !== "ok") {
    return (
      <section className="mb-4 w-full rounded-lg border border-[#e8ebf0] bg-[#f7f9fc] px-4 py-3 text-[12px] text-[#08254c]">
        <h3 className="font-semibold">{t("Digital Access")}</h3>
        <p data-testid="da-coverage-empty" className="mt-1 text-[#596b85]">
          {coverage.reason}
        </p>
      </section>
    );
  }

  const line = coverage.da_line;
  const marked = !!line && !line.in_total && line.qty_current >= coverage.suggested_blocks && coverage.suggested_blocks > 0;
  const canMark = canAct && coverage.suggested_blocks > 0 && coverage.bom_persisted && !!line && !line.in_total && !marked;
  // Só os tipos com documento: com os nove, o número que importa sumia entre zeros.
  // O tipo com item não contado aparece também, dito como tal: não é zero (#1521).
  const uncounted = new Set((coverage.uncounted ?? []).map((item) => item.doc_type).filter(Boolean));
  const weighted = Object.fromEntries(
    Object.entries(coverage.breakdown ?? {}).map(([type, value]) => [type, value.weighted]),
  );
  const breakdown = [...new Set([...Object.keys(weighted), ...uncounted])]
    .filter((type): type is string => !!type && ((weighted[type] ?? 0) > 0 || uncounted.has(type)))
    .map((type) => {
      const parts = (weighted[type] ?? 0) > 0 ? [docs(weighted[type])] : [];
      if (uncounted.has(type)) parts.push(t("not counted"));
      return `${DA_DOC_TYPE_NAMES[type as DaDocType] ?? type}: ${parts.join(" + ")}`;
    });
  const floorReasons = (coverage.floor_reasons ?? []).map((code) => floorReason(code, t));
  const floorText = floorReasons.length
    ? t("The estimate is a floor: {{reasons}}.", { reasons: floorReasons.join("; ") })
    : t("The count hit the ceiling: the estimate is a floor.");

  return (
    <section
      data-testid="da-coverage"
      className="mb-4 w-full rounded-lg border border-[#e8ebf0] bg-[#f7f9fc] px-4 py-3 text-[12px] text-[#08254c]"
    >
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="font-semibold">{t("Digital Access")}</h3>
        <span
          data-testid="da-coverage-status"
          className={cn("rounded px-1.5 text-[11px] font-semibold", STATUS_STYLE[coverage.status ?? "not_contracted"])}
        >
          {statusLabel(coverage.status, t)}
        </span>
        {coverage.basis_year && (
          <span className="text-[11px] text-[#596b85]">
            {t("Documents created in {{year}}, with the technical-user decisions of the Diagnostic", {
              year: coverage.basis_year,
            })}
          </span>
        )}
      </div>

      <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Figure
          testId="da-coverage-estimate"
          label={t("Estimate per year")}
          value={`${docs(coverage.estimate)}${coverage.complete ? "" : "+"}`}
        />
        <Figure testId="da-coverage-franchise" label={t("Franchise from licenses")} value={docs(coverage.franchise)} />
        <Figure
          testId="da-coverage-contracted"
          label={t("Contracted in the BOM")}
          value={docs(coverage.contracted_documents)}
        />
        <Figure testId="da-coverage-gap" label={t("Gap per year")} value={docs(coverage.gap)} />
      </div>

      <ul className="mt-2 space-y-0.5 text-[11px] text-[#596b85]">
        {(coverage.franchise_sources ?? []).map((source) => (
          <li key={source.sku_number} data-testid={`da-coverage-franchise-${source.sku_number}`}>
            {t("{{sku}} · {{users}} licenses × {{perUser}} documents = {{docs}}", {
              sku: source.description ?? source.sku_number,
              users: formatCount(source.users),
              perUser: docs(source.docs_per_user),
              docs: docs(source.docs),
            })}
          </li>
        ))}
        {coverage.contracted_blocks > 0 && (
          <li>
            {tp(t, coverage.contracted_blocks, "1 Digital Access block in the total", "{{n}} Digital Access blocks in the total")}
          </li>
        )}
        {!coverage.complete && (
          <li data-testid="da-coverage-floor">{floorText}</li>
        )}
      </ul>

      {breakdown.length > 0 && (
        <p data-testid="da-coverage-breakdown" className="mt-1 text-[11px] text-[#596b85]">
          {breakdown.join(" · ")}
        </p>
      )}

      <SubsequentDocumentNote
        items={coverage.subsequent_document_items ?? []}
        testId="da-coverage-subsequent"
        className="mt-1 text-[11px] text-[#596b85]"
      />

      {coverage.suggested_blocks > 0 && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <span data-testid="da-coverage-suggestion">
            {tp(
              t,
              coverage.suggested_blocks,
              "Suggestion: include 1 block of {{size}} documents (SKU {{sku}}).",
              "Suggestion: include {{n}} blocks of {{size}} documents (SKU {{sku}}).",
              { size: docs(coverage.block_size), sku: coverage.da_sku?.sku_number ?? "—" },
            )}
          </span>
          {marked && (
            <span data-testid="da-coverage-marked" className="text-[#2e6b45]">
              {tp(
                t,
                line!.qty_current,
                "1 block marked in BOM version {{version}}, outside the total until the next Pricing App upload.",
                "{{n}} blocks marked in BOM version {{version}}, outside the total until the next Pricing App upload.",
                { version: coverage.bom_version },
              )}
            </span>
          )}
          {canMark && (
            <button
              type="button"
              data-testid="da-coverage-mark"
              disabled={mark.isPending}
              onClick={() => mark.mutate(coverage)}
              className="inline-flex items-center gap-1 rounded-md border border-[#d7dde6] bg-white px-3 py-1 text-[12px] font-semibold text-[#08254c] hover:bg-[#f5f8fc] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {tp(t, coverage.suggested_blocks, "Mark 1 block for inclusion", "Mark {{n}} blocks for inclusion")}
            </button>
          )}
          {!line && (
            <span className="text-[#596b85]">
              {t("The current BOM has no Digital Access line: include the SKU in the Pricing App.")}
            </span>
          )}
        </div>
      )}
      {error && <p className="mt-1 text-[11px] text-[#b42318]">{error}</p>}
    </section>
  );
}
